import { cloneDeep, get } from "lodash";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import type { GivenNameExactness, SurnameExactness } from "@/entities/data-model";
import { AboutTheseSettingsLink, SEARCH_TIPS_EXACT_ANCHOR } from "@/shared/components/AboutTheseSettingsLink";
import { useFormAPI, useFormStore } from "@/shared/forms";
import { useFieldContext } from "../FieldContext";
import { ExactnessMultiSelect } from "./ExactnessMultiSelect";
import { getMatchLabelKey, getOptionIds, idToTranslationKey } from "./name-exactness-utils";

export const NameExactness = () => {
    const { t } = useTranslation();
    const { setFieldValue } = useFormAPI();
    const { fieldPath, exactnessFieldPath } = useFieldContext();

    invariant(exactnessFieldPath, "exactnessFieldPath is required");

    const hasName = useFormStore(({ values }) => !!get(values, fieldPath));
    const nameExactnessValue = useFormStore(({ values }) => get(values, exactnessFieldPath)) as
        | GivenNameExactness
        | SurnameExactness;

    const optionIds = getOptionIds(nameExactnessValue.flags);
    const selectedIds = optionIds.filter((id) => nameExactnessValue.flags[id as keyof typeof nameExactnessValue.flags]);

    const value = {
        isExact: nameExactnessValue.isExact,
        selectedOptionIds: selectedIds,
    };

    const options = optionIds.map((id) => ({
        id,
        label: t(idToTranslationKey[id]),
    }));

    if (!hasName) return null;

    const baseLabel = t(getMatchLabelKey(nameExactnessValue.flags));
    const label = nameExactnessValue.isExact ? baseLabel : `${baseLabel}...`;

    return (
        <ExactnessMultiSelect
            value={value}
            options={options}
            onChange={(selectedOption) => {
                const nextExactnessFieldValue = cloneDeep(nameExactnessValue);
                nextExactnessFieldValue.isExact = selectedOption.isExact;
                optionIds.forEach((key) => {
                    nextExactnessFieldValue.flags[key as keyof typeof nextExactnessFieldValue.flags] =
                        selectedOption.selectedOptionIds.includes(key);
                });
                setFieldValue(exactnessFieldPath, nextExactnessFieldValue);
            }}
            label={label}
            testId={exactnessFieldPath}
            footer={<AboutTheseSettingsLink anchor={SEARCH_TIPS_EXACT_ANCHOR} />}
        />
    );
};
